import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { startCharging } from "../api/chargingStations";
import { useStationContext } from "../context/stationContext";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { CONSTANTS } from "../constants/constants";

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const searchParams = useSearchParams();
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { stationId, setStationId, connectorId, setConnectorId } =
    useStationContext();

  const handleChargingMutation = useMutation({
    mutationFn: () => startCharging(stationId),
    onSuccess: async (data) => {
      if (data.status === "Accepted") {
        toast.success(t("checkout.startedCharging"));
        navigate(`/${lang}/transaction-details/`);
      } else {
        toast.error(t("common.stationNotAvailable"));
        navigate(`/${lang}/`);
      }
    },
    onError: (error) => {
      console.log(error);
      toast.error(t("common.wrong"));
      navigate(`/${lang}`);
    },
  });

  useEffect(() => {
    const stationIdStored = localStorage.getItem("stationId");
    const connectorIdStored = localStorage.getItem("connectorId");
    if (stationIdStored && connectorIdStored) {
      setStationId(stationIdStored);
      setConnectorId(connectorIdStored);
    } else {
      navigate(`/${lang}`);
    }
  }, []);

  useEffect(() => {
    if (stationId.length > 0) {
      const params = Object.fromEntries(searchParams[0]);
      if (params.hasOwnProperty("success") && params.success === "true") {
        setPaymentSuccess(true);
        setIsLoading(false);
        return;
      }
      setPaymentSuccess(false);
      setIsLoading(false);
      Object.values(CONSTANTS.LOCAL_STORAGE_KEYS).forEach((key) =>
        localStorage.removeItem(key)
      );
    }
  }, [stationId]);

  if (isLoading) {
    return (
      <>
        <div className="flex justify-center flex-col gap-3 items-center w-full h-screen">
          <div className="w-full h-[350px] animate-pulse rounded-3xl bg-gray-100"></div>
        </div>
        <div className="flex w-full items-center left-0 px-4 justify-center absolute bottom-0 h-[100px]">
          <div className="w-full rounded-2xl bg-red-400 animate-pulse h-[48px]"></div>
        </div>
      </>
    );
  }

  if (paymentSuccess) {
    return (
      <>
        <div className="flex justify-center flex-col gap-1 items-center w-[250px] h-screen">
          <div className="flex justify-center mb-10">
            <p className="font-semibold">
              {t("checkout.aboutToStartCharging")}
            </p>
          </div>
          <div className="flex justify-start w-full  items-center gap-4 ">
            <div className="rounded-full w-6 h-6 p-1 bg-green-500 text-white flex justify-center items-center">
              <Check size={"16px"} />
            </div>
            <div className="flex justify-start items-start flex-col">
              <p className="font-semibold text-xl">
                {t("checkout.connectorSelected")}
              </p>
              <p className="text-gray-400 text-xs">
                {t("checkout.connector")} {connectorId ?? ""}
              </p>
            </div>
          </div>
          <div className="w-[0.5px] h-[40px] bg-gray-400" />
          <div className="flex justify-start w-full  items-center gap-4 ">
            <div className="rounded-full w-6 h-6 p-1 bg-green-500 text-white flex justify-center items-center">
              <Check size={"16px"} />
            </div>
            <div className="flex justify-start items-start flex-col">
              <p className="font-semibold text-xl">
                {t("checkout.paymentSuccess")}
              </p>
              <p className="text-gray-400 text-xs">
                {t("checkout.paymentSuccesSubtitle")}
              </p>
            </div>
          </div>
          <div className="w-[0.5px] h-[40px] bg-gray-400" />
          <div className="flex justify-start w-full items-center gap-4 ">
            <div className="rounded-full w-6 h-6 p-1 bg-gray-500 text-white flex justify-center items-center">
              <Check size={"16px"} />
            </div>
            <div className="flex justify-start items-start flex-col">
              <p className="font-semibold text-xl">
                {t("checkout.startCharging")}
              </p>
              <p className="text-gray-400 text-xs">
                {t("checkout.startChargingSubtitle")}
              </p>
            </div>
          </div>
        </div>
        <div className="flex w-full items-center left-0 px-4 justify-center absolute bottom-0 h-[100px]">
          <button
            onClick={() => handleChargingMutation.mutate()}
            className="p-2 bottom-4 rounded-2xl w-full max-w-[200px] bg-green-600 hover:bg-green-700 text-white text-center cursor-pointer"
          >
            {t("checkout.continue")}
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex justify-center flex-col gap-3 items-center w-full h-screen">
        <h1>{t("checkout.paymentUnsuccessful")}</h1>
        <p>{t("checkout.paymentUnsuccessfulSubtitle")}</p>
      </div>
      <div className="flex w-full items-center left-0 px-4 justify-center absolute bottom-0 h-[100px]">
        <button
          onClick={() => navigate(`/${lang}`)}
          className="p-2 bottom-4 rounded-2xl w-full max-w-[200px] bg-red-600 hover:bg-rde-700 text-white text-center cursor-pointer"
        >
          {t("checkout.back")}
        </button>
      </div>
    </>
  );
};

export default CheckoutPage;
